import { Noto_Sans } from 'next/font/google';
import { createTheme } from '@mui/material/styles';

import { ptBR } from '@mui/x-date-pickers/locales';

import themeVariables from './theme-variables';

export const monospaceFontTheme = Noto_Sans({
  weight: ['400', '500', '600', '700'],
  subsets: ['latin'],
  display: 'swap',
  fallback: ['Roboto', 'Arial', 'sans-serif'],
  variable: '--monospace',
});

const theme = createTheme(
  {
    palette: {
      primary: {
        main: themeVariables.colors.primary,
        contrastText: themeVariables.colors.primaryContrast,
      },
      secondary: {
        dark: themeVariables.colors.secondaryDark,
        main: themeVariables.colors.secondary,
      },
      error: {
        main: themeVariables.colors.errorDark,
        dark: themeVariables.colors.errorDark,
        light: '#FAE1DD',
      },
      success: {
        main: '#14AE5C',
        light: '#DCF3E7',
      },
      info: {
        main: '#335E9E',
        light: '#335E9E30',
      },
      warning: {
        main: '#EFAD33',
        light: '#FAF5DD',
      },
            input: {
        main: '#DCDCDC',
      },
      text: {
        primary: themeVariables.colors.textPrimary,
        secondary: themeVariables.colors.textSecondary,
      },
      helperText: {
        dark: '#2D3D43',
        light: '#646464',
        disabled: '#A0A0A0',
      },
      background: { default: '#fff' },
      white: { main: themeVariables.colors.white },
      preOperational: { main: themeVariables.colors.preOperational },
      operational: { main: themeVariables.colors.operational },
      canceled: { main: themeVariables.colors.canceled },
      backgroundColor: { main: themeVariables.colors.backgroundColor },
      mediumNeutral: { main: themeVariables.colors.mediumNeutral },
      darkNeutral: { main: themeVariables.colors.darkNeutral },
      lightNeutral: { main: themeVariables.colors.lightNeutral },
    },
    typography: {
      h1: {
        fontSize: '2.329rem',
        lineHeight: '3.183rem',
      },
      h2: {
        fontSize: '2.071rem',
        lineHeight: 1.35,
      },
      h3: {
        fontSize: '1.714rem',
        lineHeight: 1.35,
      },
      h4: {
        fontSize: '1.429rem',
        lineHeight: 1.5,
      },
      h5: {
        fontSize: '1.307rem',
        lineHeight: '2rem',
      },
      h6: {
        fontSize: '1.143rem',
        lineHeight: '1.756rem',
      },
      subtitle2: {
        fontSize: '1.1rem',
        lineHeight: '1.4rem',
      },
      body1: {
        fontSize: '1rem',
        lineHeight: '1.6rem',
      },
      body2: {
        fontSize: '0.886rem',
        lineHeight: '1.371rem',
      },
      caption: {
        fontSize: '0.784rem',
        lineHeight: '1.459rem',
      },
      fontFamily: [
        '"Open Sans"',
        '"Helvetica Neue"',
        '"Helvetica"',
        '"sans-serif"',
      ].join(','),
    },
    components: {
      MuiContainer: {
        styleOverrides: {
          root: {
            maxWidth: 1440,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
            color: '#0f132a',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            fontWeight: 700,
            padding: '8px 24px',
            borderRadius: '6px',
            textTransform: 'none',
          },
          textSizeSmall: {
            fontSize: '13px',
          },
          sizeSmall: {
            padding: '5px 16px',
            fontSize: '11px',
          },
          outlined: {
            padding: '8px 24px',
            borderWidth: '1px',
            '&:hover': {
              borderWidth: '1px',
            },
          },
          contained: { boxShadow: 'none' },
        },
      },
      MuiBadge: {
        styleOverrides: {
          anchorOriginTopRightRectangular: { right: -24, top: -4 },
          root: { borderRadius: 6 },
          badge: { fontWeight: 700, padding: 10, fontSize: '0.9rem' },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 4 },
          label: {
            paddingTop: 4,
            paddingBottom: 4,
            paddingLeft: 12,
            paddingRight: 12,
            fontWeight: 700,
            fontSize: 11,
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: { minWidth: 40 },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            boxShadow: '0px 2px 7px 0px #00000024',
            '@media print': { boxShadow: 'none' },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: '#fff',
            boxShadow: '0 5px 15px -5px rgb(0 0 0 / 40%)',
            color: '#000',
            fontSize: 14,
            lineHeight: '20px',
            padding: '5px 10px',
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: themeVariables.colors.primary,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: '5px 8px',
          },
          head: {
            fontWeight: 700,
            padding: '12px 8px',
            backgroundColor: themeVariables.colors.primary,
            color: themeVariables.colors.primaryContrast,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            borderBottom: '1px solid #e7e7ed',
            '&:nth-of-type(odd)': {
              backgroundColor: '#fff',
            },
            '&:nth-of-type(even)': {
              backgroundColor: '#f8f8f8',
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontSize: '1rem',
            fontWeight: 500,
          },
          wrapped: {
            fontSize: '1rem',
            fontWeight: 600,
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          contained: { marginLeft: 5 },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          colorDefault: {
            backgroundColor: '#f3f3f3',
            color: '#0f132a',
          },
          root: { fontWeight: 700 },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: '1.2rem',
            fontWeight: 700,
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            borderTop: '1px solid #e7e7e7',
          },
          spacing: {
            padding: 15,
          },
        },
      },
    },
  },
  ptBR
);

export default theme;
